import { Cashflow } from '../types/cashflow';
import { FeeBase } from '../types/vehicle';

/**
 * 관리보수 산출 — 지급한 관리보수가 규약대로인지 대조한다 (2026-10-26 일반화).
 *
 * 실제 규약이 둘 다 단순 「약정총액 × 요율」이 아니었다.
 *   · 개인투자조합 1호 — 연 2%, 처음 2년은 결성 때 선취, 이후는 청산 때 후취
 *   · 벤처투자조합 1호 — 투자기간 약정액의 2%, 회수기간 투자잔액의 2%, 선취 3년
 *
 * 규칙
 *   · 기준은 날마다 정한다 — 투자기간 종료일(investEndOn) 다음 날부터 baseAfter
 *   · invested(투자잔액) = 그날까지 나간 투자 원금 − 끝난 건(전량 회수 · 상각)의 원금
 *   · 선취가 있으면 첫 지급이 선취분 = 요율 × 결성일 기준액 × 연수. 그 뒤 일할은 선취 기간이 끝난 날부터
 */
export interface FeeTerms {
  rate: number;
  base: FeeBase;
  baseAfter?: FeeBase | null;
  investEndOn?: string | null;
  prepaidYears?: number | null;
  formedOn?: string | null;
  committed: number;
}
export interface DealEnd { dealId: number; endedOn: string | null }
export interface FeeRow { f: Cashflow; from: string; days: number; expected: number; prepaid: boolean; ok: boolean }

const DAY = 86_400_000;
const add = (s: string, n: number) => new Date(new Date(`${s}T00:00:00Z`).getTime() + n * DAY).toISOString().slice(0, 10);
const diff = (a: string, b: string) => Math.round((new Date(`${b}T00:00:00Z`).getTime() - new Date(`${a}T00:00:00Z`).getTime()) / DAY);
export const addYears = (s: string, n: number) => `${Number(s.slice(0, 4)) + n}${s.slice(4)}`;

/** 그날의 기준액 */
export function baseOn(day: string, t: FeeTerms, flows: Cashflow[], ends: DealEnd[]): number {
  const kind = t.investEndOn && day > t.investEndOn ? (t.baseAfter ?? t.base) : t.base;
  if (kind === 'committed') return t.committed;
  if (kind === 'paid_in') return flows.filter((f) => f.kind === 'contribution' && f.occurredOn <= day).reduce((s, f) => s + f.amount, 0);
  const ended = new Set(ends.filter((e) => e.endedOn !== null && e.endedOn <= day).map((e) => e.dealId));
  return flows.filter((f) => f.kind === 'investment' && f.occurredOn <= day && !(f.dealId !== null && ended.has(f.dealId)))
    .reduce((s, f) => s + f.amount, 0);
}

/** from 부터 days 일 동안 일할 산출액 */
export function accrue(from: string, days: number, t: FeeTerms, flows: Cashflow[], ends: DealEnd[]): number {
  let sum = 0;
  for (let k = 0; k < days; k++) sum += baseOn(add(from, k), t, flows, ends);
  return Math.round((t.rate * sum) / 365);
}

/** 관리보수 지급 줄마다 규약 산출액 — 1% 또는 1천 원 안이면 일치 */
export function feeRows(fees: Cashflow[], t: FeeTerms, flows: Cashflow[], ends: DealEnd[]): FeeRow[] {
  const sorted = [...fees].sort((a, b) => a.occurredOn.localeCompare(b.occurredOn));
  const prepaidEnd = t.prepaidYears && t.formedOn ? addYears(t.formedOn, t.prepaidYears) : null;
  return sorted.map((f, i) => {
    let from: string, days: number, expected: number, prepaid = false;
    if (i === 0 && prepaidEnd && t.formedOn) {
      prepaid = true;
      from = t.formedOn;
      days = diff(t.formedOn, prepaidEnd);
      expected = Math.round(t.rate * baseOn(t.formedOn, t, flows, ends) * t.prepaidYears!);
    } else {
      const prev = i > 0 ? sorted[i - 1].occurredOn : (t.formedOn ?? f.occurredOn);
      from = prepaidEnd && prev < prepaidEnd ? prepaidEnd : prev;
      days = Math.max(1, diff(from, f.occurredOn));
      expected = accrue(from, days, t, flows, ends);
    }
    return { f, from, days, expected, prepaid, ok: Math.abs(f.amount - expected) <= Math.max(1000, expected * 0.01) };
  });
}
